import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Chip,
    Grid,
    Card,
    CardMedia,
    Avatar,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    People as PeopleIcon,
    AccessTime as TimeIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckIcon,
    Visibility as PreviewIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { databases, functions, COLLECTIONS, DATABASE_ID, Query, ID, getUserAvatar } from '../../lib/appwrite';
import { formatDuration } from '../../lib/format';

import { ErrorBoundary } from '../../components/common/ErrorBoundary';
import {
    courseDetailFields,
    moduleFields,
    lessonFields,
    enrollmentFields,
    progressFields,
    userPublicFields,
    measureQueryPerformance,
    fetchWithSelect,
    queryCache,
    buildCacheKey
} from '../../lib/queryOptimizer';

// Motion wrapper
const MotionBox = motion.create(Box);



/**
 * Course Detail Page
 * 
 * MUI-based page showing course information, curriculum, and enrollment button.
 * OPTIMIZED: Uses Query.select() and caching to reduce response size by ~60%
 */
export function CourseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [expanded, setExpanded] = useState('m1');

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    // State
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);

    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                if (!id) return;

                // 1. Fetch Course (getDocument doesn't support Query.select)
                const courseDoc = await measureQueryPerformance(
                    'fetchCourseDetail',
                    () => databases.getDocument(DATABASE_ID, COLLECTIONS.COURSES, id)
                );

                // 2. Fetch Modules (OPTIMIZED with Query.select)
                const modulesRes = await measureQueryPerformance(
                    'fetchModules',
                    () => fetchWithSelect(
                        (q) => databases.listDocuments(DATABASE_ID, COLLECTIONS.MODULES, q),
                        [
                            Query.equal('courseId', id),
                            Query.orderAsc('order')
                        ],
                        moduleFields
                    )
                );

                // 3. Fetch Lessons (OPTIMIZED with Query.select)
                const moduleIds = modulesRes.documents.map(m => m.$id);
                let allLessons = [];

                if (moduleIds.length > 0) {
                    const lessonsRes = await measureQueryPerformance(
                        'fetchLessons',
                        () => fetchWithSelect(
                            (q) => databases.listDocuments(DATABASE_ID, COLLECTIONS.LESSONS, q),
                            [
                                Query.equal('moduleId', moduleIds),
                                Query.orderAsc('order'),
                                Query.limit(500)
                            ],
                            lessonFields
                        )
                    );
                    allLessons = lessonsRes.documents;

                    // Warn if we hit the limit
                    if (allLessons.length === 500) {
                        console.warn('⚠️ Lesson query hit 500 limit for course:', id);
                    }
                }

                // 4. Fetch Real Student Count (OPTIMIZED)
                // Only query total, not full documents
                let studentsCount = courseDoc.studentsCount || 0;

                if (user) {
                    try {
                        const enrollmentsCountRes = await measureQueryPerformance(
                            'fetchEnrollmentCount',
                            () => fetchWithSelect(
                                (q) => databases.listDocuments(DATABASE_ID, COLLECTIONS.ENROLLMENTS, q),
                                [
                                    Query.equal('courseId', id),
                                    Query.limit(1) // We only need the 'total' property
                                ],
                                ['$id'] // Minimal field selection
                            )
                        );
                        studentsCount = enrollmentsCountRes.total || studentsCount;
                    } catch (enrollErr) {
                        console.warn('Could not fetch live enrollment count:', enrollErr);
                        // Continue with cached count
                    }
                }

                // 4b. User Progress (OPTIMIZED with Query.select)
                let completedLessonIds = [];
                if (user && allLessons.length > 0) {
                    try {
                        const progressRes = await measureQueryPerformance(
                            'fetchProgress',
                            () => fetchWithSelect(
                                (q) => databases.listDocuments(DATABASE_ID, COLLECTIONS.PROGRESS, q),
                                [
                                    Query.equal('userId', user.$id),
                                    Query.equal('lessonId', allLessons.map(l => l.$id))
                                ],
                                progressFields
                            )
                        );
                        completedLessonIds = progressRes.documents.map(p => p.lessonId);
                    } catch (e) {
                        console.error('Failed to load progress:', e);
                        // Continue without progress if fail
                    }
                }

                // Stitch structure
                const fullModules = modulesRes.documents.map(m => ({
                    id: m.$id,
                    title: m.title,
                    lessons: allLessons
                        .filter(l => l.moduleId === m.$id)
                        .map(l => ({
                            id: l.$id,
                            title: l.title,
                            duration: formatDuration(l.duration),
                            completed: completedLessonIds.includes(l.$id) // Real progress
                        }))
                }));

                // 5. Fetch Instructor Details (OPTIMIZED with caching)
                let instructorData = {
                    name: courseDoc.instructorName || 'Unknown Instructor',
                    avatar: '',
                };

                if (courseDoc.instructorId) {
                    try {
                        // Use cache for instructor data (TTL: 5 minutes)
                        const cacheKey = buildCacheKey('instructor', courseDoc.instructorId);
                        const instructorDocs = await queryCache.getOrFetch(
                            cacheKey,
                            () => measureQueryPerformance(
                                'fetchInstructor',
                                () => fetchWithSelect(
                                    (q) => databases.listDocuments(DATABASE_ID, COLLECTIONS.USERS, q),
                                    [Query.equal('userId', courseDoc.instructorId), Query.limit(1)],
                                    userPublicFields
                                )
                            ),
                            300000 // 5 minutes cache
                        );

                        if (instructorDocs.documents && instructorDocs.documents.length > 0) {
                            const instDoc = instructorDocs.documents[0];
                            instructorData = {
                                name: instDoc.name || instructorData.name,
                                avatar: getUserAvatar(instDoc)
                            };
                        }
                    } catch (err) {
                        console.warn('Failed to fetch instructor profile:', err);
                        // Fallback to denormalized instructorName from course doc
                    }
                }

                setCourse({
                    id: courseDoc.$id,
                    title: courseDoc.title,
                    description: courseDoc.description,
                    category: courseDoc.category || 'General',
                    price: courseDoc.price || 0, // Ensure price is present
                    thumbnail: courseDoc.thumbnail || 'https://images.unsplash.com/photo-1593720219276-0b1eacd0aef4?w=1200',
                    instructorId: courseDoc.instructorId, // For owner detection
                    instructor: instructorData,
                    lessonsCount: allLessons.length,
                    studentsCount: studentsCount,
                    duration: 'Self-paced',
                    isEnrolled: false,
                    modules: fullModules,
                });

            } catch (error) {
                console.error('Failed to load course:', error);
                toast.error('Could not load course details');
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [id, user]); // Added user dependency

    // Check enrollment status - Moved to top level to comply with Rules of Hooks
    useEffect(() => {
        const checkEnrollment = async () => {
            // Internal safety check instead of conditional hook call
            if (!isAuthenticated || !course || !user) return;

            try {
                const response = await databases.listDocuments(
                    DATABASE_ID,
                    COLLECTIONS.ENROLLMENTS,
                    [
                        Query.equal('userId', user.$id),
                        Query.equal('courseId', course.id)
                    ]
                );

                if (response.documents.length > 0) {
                    setCourse(prev => ({ ...prev, isEnrolled: true }));
                }
            } catch (error) {
                console.error('Failed to check enrollment:', error);
            }
        };

        checkEnrollment();
    }, [course?.id, user?.$id, isAuthenticated]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!course) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <Typography>Course not found</Typography>
            </Box>
        );
    }

    const handleStartLearning = () => {
        if (course?.modules?.[0]?.lessons?.[0]) {
            navigate(`/learn/${course.id}/${course.modules[0].lessons[0].id}`);
        } else {
            toast.error('Course has no lessons yet.');
        }
    };

    const handleEnroll = async () => {
        if (!isAuthenticated) {
            toast.error('Please login to enroll in this course');
            navigate('/login');
            return;
        }

        try {
            setEnrolling(true);

            // Double check enrollment
            if (course.isEnrolled) {
                setEnrolling(false);
                return handleStartLearning();
            }

            // 1. Check if Free
            if (!course.price || course.price === 0) {
                // Direct Enroll logic for free courses
                await databases.createDocument(
                    DATABASE_ID,
                    COLLECTIONS.ENROLLMENTS,
                    ID.unique(),
                    {
                        userId: user.$id,
                        courseId: course.id,
                        enrolledAt: new Date().toISOString()
                    }
                );

                setCourse(prev => ({
                    ...prev,
                    isEnrolled: true,
                    studentsCount: (prev.studentsCount || 0) + 1
                }));

                toast.success('Successfully enrolled!');
                handleStartLearning();
                setEnrolling(false);

            } else {
                // 2. Paid Course - Use Payment Service
                toast.loading('Redirecting to payment...');

                const execution = await functions.createExecution(
                    import.meta.env.VITE_APPWRITE_FUNCTION_ID,
                    JSON.stringify({
                        courseId: id,
                        successUrl: window.location.origin + '/payment-success',
                        cancelUrl: window.location.href
                    }),
                    false, // Async
                    '/checkout', // Path
                    'POST' // Method
                );

                if (execution.status === 'completed') {
                    const response = JSON.parse(execution.responseBody);
                    console.log('Payment Response:', response); // DEBUG LOG
                    if (response.ok && response.url) {
                        window.location.href = response.url;
                    } else {
                        toast.error(response.error || 'Payment initialization failed');
                        setEnrolling(false);
                    }
                } else {
                    toast.error('Server execution failed');
                    setEnrolling(false);
                }
            }

        } catch (error) {
            console.error('Enrollment error:', error);
            toast.error('Failed to enroll. Please try again.');
            setEnrolling(false);
        }
    };

    const totalLessons = course.modules.reduce((acc, m) => acc + m.lessons.length, 0);

    return (
        <Box sx={{ py: { xs: 4, md: 6 } }} >
            <Container maxWidth="lg">
                {/* Course Header */}
                <Grid container spacing={4} sx={{ mb: 6 }}>
                    <Grid size={{ xs: 12, md: 7 }}>
                        <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Chip label={course.category} color="primary" sx={{ mb: 2 }} />

                            <Typography variant="h3" fontWeight={700} gutterBottom>
                                {course.title}
                            </Typography>

                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mb: 3, lineHeight: 1.8 }}
                            >
                                {course.description}
                            </Typography>

                            {/* Course Stats */}
                            <Stack
                                direction="row"
                                spacing={3}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mb: 3 }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PlayIcon color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {totalLessons} lessons
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PeopleIcon color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {course.studentsCount.toLocaleString()} students
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <TimeIcon color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {course.duration}
                                    </Typography>
                                </Stack>
                            </Stack>

                            {/* Instructor */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                                <Avatar
                                    src={course.instructor.avatar}
                                    alt={course.instructor.name}
                                >
                                    {course.instructor.name.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Instructor
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {course.instructor.name}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* CTA - Role-based access control */}
                            {(() => {
                                // Best Practice: Owner/Admin get Preview access without enrollment
                                const isCourseOwner = user && course?.instructorId === user.$id;
                                const isAdmin = user?.role === 'admin';

                                if (isCourseOwner || isAdmin) {
                                    // Owner/Admin: Preview without enrollment
                                    return (
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            onClick={handleStartLearning}
                                            startIcon={<PreviewIcon />}
                                            color="secondary"
                                            sx={{ width: { xs: '100%', md: 'auto' } }}
                                        >
                                            {isCourseOwner ? 'Preview Your Course' : 'Admin Preview'}
                                        </Button>
                                    );
                                } else if (course.isEnrolled) {
                                    // Enrolled student: Continue
                                    return (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={handleStartLearning}
                                            startIcon={<PlayIcon />}
                                            sx={{ width: { xs: '100%', md: 'auto' } }}
                                        >
                                            Continue Learning
                                        </Button>
                                    );
                                } else {
                                    // Not enrolled: Show enroll button
                                    const formatPrice = (price) => {
                                        return new Intl.NumberFormat('id-ID', {
                                            style: 'currency',
                                            currency: 'IDR',
                                            minimumFractionDigits: 0
                                        }).format(price);
                                    };

                                    return (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={handleEnroll}
                                            disabled={enrolling}
                                            startIcon={enrolling ? <CircularProgress size={20} /> : <PlayIcon />}
                                            sx={{ width: { xs: '100%', md: 'auto' } }}
                                        >
                                            {course.price && course.price > 0
                                                ? `Buy Now - ${formatPrice(course.price)}`
                                                : 'Enroll Now - Free'}
                                        </Button>
                                    );
                                }
                            })()}
                        </MotionBox>
                    </Grid>

                    {/* Thumbnail */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <MotionBox
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <CardMedia
                                    component="img"
                                    image={course.thumbnail}
                                    alt={course.title}
                                    sx={{ aspectRatio: '16/9' }}
                                />
                            </Card>
                        </MotionBox>
                    </Grid>
                </Grid>

                {/* Curriculum */}
                <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
                        Course Curriculum
                    </Typography>

                    {course.modules.map((module, index) => (
                        <Accordion
                            key={module.id}
                            expanded={expanded === module.id}
                            onChange={handleChange(module.id)}
                            sx={{ mb: 1 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Module {index + 1}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {module.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {module.lessons.length} lessons
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <List disablePadding>
                                    {module.lessons.map((lesson) => (
                                        <ListItem
                                            key={lesson.id}
                                            divider
                                            sx={{ py: 1.5, px: 3 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                {lesson.completed ? (
                                                    <CheckIcon color="success" />
                                                ) : (
                                                    <PlayIcon color="action" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={lesson.title}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {lesson.duration}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Container>
        </Box >
    );
}

export default function CourseDetailWithErrorBoundary() {
    return (
        <ErrorBoundary>
            <CourseDetail />
        </ErrorBoundary>
    );
}
